<template>
  <NuxtLink
    ref="$el"
    class="sub-industry-card"
    :to="to"
    @click="onClick"
  >
    <UiComponentWrapper class="head">
      <UiComponentImage
        v-if="props.image && !!imageSize"
        :key="`sub-industry-card-image-${imageSize}`"
        :alt="props.image.alt"
        :expected-size="imageSize"
        :height="props.image.height"
        :source="props.image.url"
        :width="props.image.width"
      />
    </UiComponentWrapper>
    <UiComponentWrapper class="body">
      <UiComponentHeading :level="3">
        {{ props.title }}
      </UiComponentHeading>
      <UiComponentParagraph>{{ props.text }}</UiComponentParagraph>
      <AtomButton type="text">
        <slot />
        <UiComponentIcon type="arrow-right" />
      </AtomButton>
    </UiComponentWrapper>
  </NuxtLink>
</template>

<script setup>
// Composables
const { track } = useAnalyticsJS()
const localePath = useLocalePath()
const { params } = useRoute()

// Props
const props = defineProps({
  image: {
    required: true,
    type: Object,
  },
  title: {
    required: true,
    type: String,
  },
  text: {
    required: true,
    type: String,
  },
  slug: {
    required: true,
    type: String,
  },
})

// Refs
const $el = ref(null)
const imageSize = ref(null)
const resizeObserver = ref(null)

// Computed Properties
const to = computed(() => {
  return localePath({
    name: 'industries-slug-tag',
    params: {
      slug: params.slug,
      tag: props.slug,
    },
  })
})

// Methods
function onClick() {
  track('Button clicked', {
    action: to.value,
    category: 'Button',
    label: 'Sub-industries section CTA',
  })
}

function onObserve() {
  const { offsetWidth } = $el.value.$el.querySelector('.head')
  imageSize.value = offsetWidth
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  resizeObserver.value.disconnect()
  resizeObserver.value = null
})

onMounted(() => {
  resizeObserver.value = new ResizeObserver(onObserve)
  resizeObserver.value.observe($el.value.$el.querySelector('.head'))
})
</script>

<style lang="postcss" scoped>
.sub-industry-card {
  @apply flex flex-col gap-4 p-8 bg-white border-2 border-grey-blue-200 rounded-[40px] transition-all duration-500 ease-in-out cursor-pointer;

  @screen md {
    @apply flex-row items-center gap-10;
  }

  @screen xl {
    @apply flex-col items-start gap-4;
  }

  &:hover {
    @apply border-transparent shadow-light3;
  }

  .head {
    @apply bg-grey-blue-100 aspect-[620/433] rounded-xs overflow-hidden;

    picture {
      @apply rounded-none;
    }
  }

  .body {
    @apply flex-col;

    .h3 {
      @apply text-xl leading-7;

      @screen mdminus {
        @apply text-2xl leading-8;
      }

      @screen mdplus {
        @apply text-2xl leading-8;
      }
    }

    p {
      @apply mt-2 text-grey-blue-600 line-clamp-3;
    }

    .button {
      @apply self-start h-7 mt-4 p-0 font-normal text-origin text-livestorm-blue-700 leading-7;
    }
  }
}
</style>
